import {
  ActionSheetButton,
  IonActionSheet,
  IonButtons,
  IonChip,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { lockClosed } from 'ionicons/icons';
import { sortBy } from 'lodash';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticateWithPinCodeMutation } from '../../api/graphql/generated';
import { SaveStatusToast } from '../../components/toast/toast.gql';
import { useUserPinCodeConfirmation } from '../../components/user/user-pin-code-confirm';
import { useDirectConnectSetSession } from '../cache/direct-connect-session-cache';
import { useDirectConnectHealthGateContext } from '../gate/direct-connect-health-gate';
import { DirectConnectHealthUserInfo } from '../model/direct-connect.model';

interface DirectConnectUserSelectProps {
  onSelect: (user?: DirectConnectHealthUserInfo) => void;
}

interface DirectConnectUserSelectModalProps
  extends DirectConnectUserSelectProps {
  isOpen: boolean;
  leftButtons?: ReactElement;
}

const DirectConnectUserSelectModal = (
  props: DirectConnectUserSelectModalProps
) => {
  const { isOpen, onSelect, leftButtons } = props;
  const { i18n } = useTranslation();

  const onSelectInner = useCallback(
    (user?: DirectConnectHealthUserInfo) => {
      onSelect(user);
    },
    [onSelect]
  );

  return (
    <IonModal isOpen={isOpen} backdropDismiss={false}>
      <IonPage>
        <IonHeader className="primary-header">
          <IonToolbar className="primary-toolbar">
            <IonButtons slot="start">{leftButtons}</IonButtons>
            <IonTitle>{i18n.t('common:direct_connect_select_user')}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <DirectConnectUserSelect onSelect={onSelectInner} />
        </IonContent>
      </IonPage>
    </IonModal>
  );
};

const DirectConnectUserSelect = (props: DirectConnectUserSelectProps) => {
  const { onSelect } = props;

  const { i18n } = useTranslation();

  const [selectedUser, setSelectedUser] = useState<
    DirectConnectHealthUserInfo | undefined
  >();

  const [showUserActionSheet, setShowUserActionSheet] = useState(false);

  const { healthStatus } = useDirectConnectHealthGateContext();
  const [authenticateWithPinCode, { loading, error }] =
    useAuthenticateWithPinCodeMutation();

  const setDirectConnectSession = useDirectConnectSetSession();

  const users = useMemo(() => {
    return healthStatus?.users ?? [];
  }, [healthStatus]);

  const onPinCodeConfirm = useCallback(
    async (pinCode?: string) => {
      if (!selectedUser) {
        return;
      }

      const { userId } = selectedUser;

      try {
        const result = await authenticateWithPinCode({
          variables: {
            input: {
              userId,
              // use empty string if a pin code isn't provided
              pinCode: pinCode ?? '',
            },
          },
        });

        const response = result.data?.authenticateWithPinCode;

        if (response?.sessionId) {
          await setDirectConnectSession({
            sessionId: response.sessionId,
            expiresAt: response.expiresAt,
          });

          onSelect(selectedUser);
        }
      } catch (err) {
        // do nothing as we will present a toast if this fails
      }
    },
    [authenticateWithPinCode, onSelect, selectedUser, setDirectConnectSession]
  );

  const { present: presentPinCodeConfirmation } = useUserPinCodeConfirmation({
    onPinCodeConfirm,
    submitLabel: 'common:login',
  });

  const onLogin = useCallback(
    (user: DirectConnectHealthUserInfo) => {
      if (user?.hasPinCode) {
        presentPinCodeConfirmation();
      } else {
        // if the user doesn't have a pin code we will still perform an auth
        // to be consistent, however we will do so without a pin code
        onPinCodeConfirm();
      }
    },
    [onPinCodeConfirm, presentPinCodeConfirmation]
  );

  const actionSheetButtons = useMemo((): ActionSheetButton[] => {
    return [
      {
        text: i18n.t('common:login'),
        handler: () => {
          if (selectedUser) {
            onLogin(selectedUser);
          }
        },
      },
      {
        text: i18n.t('common:cancel'),
        role: 'cancel',
      },
    ];
  }, [i18n, selectedUser, onLogin]);

  const activeUsers = useMemo(() => {
    return sortBy(
      users.filter((u) => u.active),
      'name'
    );
  }, [users]);

  return (
    <>
      <IonList>
        {activeUsers.map((user) => {
          return (
            <IonItem
              key={user.userId}
              onClick={() => {
                setSelectedUser(user);
                setShowUserActionSheet(true);
              }}
              style={{ cursor: 'pointer' }}
            >
              <IonLabel>{user.name}</IonLabel>
              {user.hasPinCode && (
                <IonIcon size="small" icon={lockClosed} color="primary" />
              )}
              <IonChip slot="end" color="primary" outline={true}>
                {user.role}
              </IonChip>
            </IonItem>
          );
        })}
      </IonList>
      <IonActionSheet
        isOpen={showUserActionSheet}
        onDidDismiss={() => setShowUserActionSheet(false)}
        buttons={actionSheetButtons}
      />
      <SaveStatusToast
        showOnly="error"
        error={error}
        loading={loading}
        defaultErrorMessageKey={'common:login_failed'}
      />
    </>
  );
};

export { DirectConnectUserSelectModal, DirectConnectUserSelect };
